<template>
    <wrapper-features-templates-one-column
    :hideWhiteBoxOnLoad="showLoader || (!applyErrorState && !policiesTableData.length)"
    :showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState"
	:errorStateMessage="t('bankassurance.policies.emptyState.error')"
    @backOrCloseClicked="emit('backOrCloseClicked')">
		<!-- *********************************************
		*                     Content                    *
		********************************************** -->
		<transition name="common-enter-animation" mode="out-in" appear>
			<div v-if="!showLoader && !applyErrorState && policiesTableData.length" class="content-wrapper prevent-auto-margins">
				<table-generic
				:headerData="policiesHeaderData"
				:tableStyles="policiesTableStyles"
				:tableData="policiesTableData"
				@rowClicked="policyClickHandler($event)"
				@downloadClicked="downloadClickedHandler"/>
			</div>
		</transition>
		<template #empty-state-slot>
			<!-- Empty-state -->
			<empty-state-generic
			v-if="!showLoader && !applyErrorState && !policiesTableData.length"
			illustration="emptyState/bankassurance-policies"
			:title="t('bankassurance.policies.emptyState.title')"
			:description="t('bankassurance.policies.emptyState.description')"/>
		</template>
	</wrapper-features-templates-one-column>
	<!-- Details Modal -->
	<policy-details-modal
	v-if="showDetailsModal"
	:selectedPolicy="selectedPolicy"
	@hideMe="showDetailsModal = false"/>
	<!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
	<!-- FeedbackToast -->
	<toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
/* Details Component */
import PolicyDetailsModal from '@/components/features/bankassurance/policies/PolicyDetailModal'

/* Assets & Helpers */
import { ref, computed, onBeforeUnmount } from 'vue'
import { getDate } from '@/assets/scripts/dateBuilders'
import { iconMapper, iconColorMapper } from '@/assets/scripts/mappers/policies'
import { createDownloadPolicyUrl } from '@/network/api/insurance.api.js'
import { emptyObjectConstructor, errorObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* mapActions & mapGetters */
const { requestPoliciesList, clearFeatureStates } = mapActions(moduleMapper.BANKASSURANCE_POLICIES)
const { getPoliciesList } = mapGetters(moduleMapper.BANKASSURANCE_POLICIES)
const { getMobileAppState } = mapGetters()

/* Data */
const showOverlayLoader = ref(false)
const showLoader = ref(false)
const applyErrorState = ref(false)
const selectedPolicy = ref()
const downloadLink = ref(null)
const showDetailsModal = ref(false)

/*************************************************
*                 Requests Logic                 *
*************************************************/
const requestPolicyList = async () => {
	cancelAllPendingRequests()
	applyErrorState.value = false
	showFeedbackToast.value = false
	showLoader.value = true

	try {
		await requestPoliciesList()
		showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		applyErrorState.value = true
		errorHandler(error.response.data, requestPolicyList)
	}
}

/*************************************************
*             Sent Requests Data                 *
*************************************************/
const policiesHeaderData = [
	{ label: t('bankassurance.policies.tableHeader.type'), showInMobile: { groupWith: ['number', 'date'] }, dataAttr: 'type', rowCellType: 'title' },
	{ label: t('bankassurance.policies.tableHeader.number'), dataAttr: 'description', rowCellType: 'number' },
	{ label: t('bankassurance.policies.tableHeader.startDate'), dataAttr: 'date', rowCellType: 'date' },
	{ label: t('bankassurance.policies.tableHeader.status'), showInMobile: true, dataAttr: 'status', rowCellType: 'status' },
	{ dataAttr: 'download', rowCellType: 'download', showInMobile: true }
]

const policiesTableStyles = [
	null,
	getMobileAppState.value ? null : { 'text-align': 'end' },
	{ 'text-align': 'end' },
	{ width: '15%', 'padding-left': '32px' },
	null
]

const policiesTableData = computed(() => {
	return getPoliciesList.value.map((policy, index) => {
		return [
			{ value: policy.insuranceId, dataAttr: 'id' },
			{
				type: 'title',
				value: policy.insuranceDescription,
				dataAttr: 'type'
			},
			{
				type: 'description',
				value: getMobileAppState.value ? t('bankassurance.policies.mobile.number') + policy.policyId : policy.policyId,
				dataAttr: 'number'
			},
			{
				value: {
					content: getMobileAppState.value ? t('bankassurance.policies.mobile.startDate') + getDate(policy.insuranceStartDate) : getDate(policy.insuranceStartDate),
					classes: getMobileAppState.value ? 'small secondary-color' : 'capitalize'
				},
				dataAttr: 'date'
			},
			{
				type: 'status',
				value: {
					text: policy.policyStatus.description || '',
					icon: iconMapper(policy.policyStatus.name),
					classes: iconColorMapper(policy.policyStatus.name)
				},
				dataAttr: 'status'
			},
            { type: 'download', dataAttr: 'download', hidden: false }
		]
	})
})

const policyClickHandler = row => {
	selectedPolicy.value = getPoliciesList.value.find(policy => policy.insuranceId === row.rowId)

	showDetailsModal.value = true
}

const downloadClickedHandler = rowId => {
    downloadLink.value = createDownloadPolicyUrl(rowId)
}

/*************************************************
*              Success/Error Handlers            *
*************************************************/
const errorHandler = (response, retryFn) => {
	showOverlayLoader.value = false
	showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(response, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                  Toast Handlers                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

requestPolicyList()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
.content-wrapper {
	@include commonEnteringAnimation;
	width: 100%;
}
</style>
